import { showToast } from '../../engine/toast';
import { openModal, type ModalHandle, type ModalOptions } from './modal';
import { SMALL_BTN_CLS } from './admin-ui';
import { initialAdminState, type PaneCtx, type PaneId, type PaneModule } from './panes/types';
import { overviewPane } from './panes/overview';
import { playersPane } from './panes/players';
import { invoicesPane } from './panes/invoices';
import { cmsPane } from './panes/cms';
import { companyPane, metaPane } from './panes/settings-forms';
import { keysPane } from './panes/keys';
import { paymentsPane } from './panes/payments';
import { SIDE_MENU_OPEN, mountSideMenu, setSideMenuChip, sideMenuBarHtml } from '../../engine/side-menu';

/**
 * In-app admin, replacing the separate Kottster panel.
 *
 * The template merge retired Kottster: it removed the service from both
 * compose files, dropped the `build-admin` CI job, and deleted the entire
 * `x-admin-token` path from AdminAccessGuard — which is the only credential
 * the old panel had. Its Meta Tags page authenticated with exactly that
 * header, so it had been returning 401 since the merge landed.
 *
 * Reached from Settings, gated on the session's role. That gate is a courtesy
 * so the entry point is not dangled at players — every `/api/admin/*` route is
 * re-checked server-side by AdminAccessGuard on each call, so hiding the
 * button grants nothing and showing it gives nothing away.
 *
 * STRUCTURE. This file is the shell only: the header, the rail, the two
 * delegated listeners, and the busy/disposed bookkeeping. Each pane is its own
 * module under `panes/` exporting `html(ctx)` plus optional `load`/`onAction`/
 * `onSubmit`, and the shell walks the registry rather than carrying a switch.
 * The split happened when the screen outgrew one file; the two-listener
 * arrangement survived it because dialogs mount on `document.body` (see
 * `modal.ts`), so their events never reach this root.
 */

const MODULES: PaneModule[] = [overviewPane, playersPane, invoicesPane, cmsPane, companyPane, metaPane, keysPane, paymentsPane];
const BY_ID = new Map<PaneId, PaneModule>(MODULES.map((m) => [m.id, m]));

export function renderAdminScreen(root: HTMLElement, onExit?: () => void): () => void {
  let disposed = false;
  let modal: ModalHandle | null = null;
  const state = initialAdminState();

  root.innerHTML = `
    <div class="w-full h-full flex flex-col">
      <div class="flex items-center justify-between gap-2 px-3 py-2 border-b border-border shrink-0">
        <h1 class="font-heading text-gold text-base">Admin</h1>
        <button type="button" data-action="exit" class="${SMALL_BTN_CLS}">Back to settings</button>
      </div>
      <div class="flex-1 min-h-0 overflow-y-auto">
        <div class="p-4 sm:p-6 max-w-5xl mx-auto flex flex-col sm:flex-row gap-4 sm:gap-6 pb-10">
          ${SIDE_MENU_OPEN}
            ${sideMenuBarHtml(MODULES[0].label, 'Admin sections', 'admin-rail')}
            <nav aria-label="Admin sections" id="admin-rail"
              class="side-menu-panel shrink-0 sm:w-40 flex sm:flex-col gap-1 sm:overflow-visible sm:sticky sm:top-0 sm:self-start"></nav>
          </div>
          <div class="flex-1 min-w-0" id="admin-pane"></div>
        </div>
      </div>
    </div>
  `;

  const railEl = root.querySelector('#admin-rail') as HTMLElement;
  const paneEl = root.querySelector('#admin-pane') as HTMLElement;

  function closeModal(): void {
    modal?.close();
    modal = null;
  }

  const ctx: PaneCtx = {
    state,
    reload: (pane) => loadPane(pane),
    withBusy: (fn, failure) => withBusy(fn, failure),
    openModal(options) {
      // One dialog at a time, and the shell owns the handle so teardown and
      // pane switches can close it — a modal lives on document.body, so
      // nothing else would ever remove it.
      closeModal();
      modal = openModal({
        ...options,
        onClose: () => {
          modal = null;
          options.onClose?.();
        },
      } as ModalOptions);
      return modal;
    },
    repaint: () => render(),
    disposed: () => disposed,
  };

  function paintRail(): void {
    setSideMenuChip(root, BY_ID.get(state.activePane)?.label ?? '');
    railEl.innerHTML = MODULES.map(
      (p) => `
        <button type="button" data-action="pick-pane" data-pane="${p.id}"
          aria-current="${p.id === state.activePane ? 'page' : 'false'}"
          class="w-full text-left min-h-11 px-3 py-2 border-l-[3px] text-[11px] uppercase tracking-[0.14em] transition-colors duration-[120ms] touch-manipulation ${
            p.id === state.activePane
              ? 'border-l-gold bg-surface-2 text-text'
              : 'border-l-transparent text-text-dim hover:text-text'
          }">${p.label}</button>`,
    ).join('');
  }

  function paintPane(): void {
    const pane = BY_ID.get(state.activePane);
    if (pane) paneEl.innerHTML = pane.html(ctx);
  }

  function render(): void {
    paintRail();
    paintPane();
  }

  /** Each pane loads only what it shows; a failure degrades that pane, not the screen. */
  async function loadPane(pane: PaneId): Promise<void> {
    try {
      await BY_ID.get(pane)?.load?.(ctx);
    } catch (err) {
      if (disposed) return;
      showToast(err instanceof Error ? err.message : 'Could not load that section.', 'error');
    }
    if (!disposed) render();
  }

  /**
   * Runs one mutation at a time and surfaces failures.
   *
   * The repaint at the end is load-bearing. `state.busy` is what disables
   * buttons mid-request, and panes naturally repaint INSIDE this callback
   * (right after the server confirms) — at which point `busy` is still true,
   * so everything renders disabled and nothing ever re-enables it. That is
   * what made the feature switches one-way: the first toggle worked, redrew
   * itself disabled, and ignored every click after. Repainting once more
   * after `busy` clears is the fix, and it covers the Company and Meta Save
   * buttons, which had the same latent problem.
   */
  async function withBusy(fn: () => Promise<void>, failure: string): Promise<void> {
    if (state.busy) return;
    state.busy = true;
    try {
      await fn();
    } catch (err) {
      if (!disposed) showToast(err instanceof Error ? err.message : failure, 'error');
    } finally {
      state.busy = false;
      if (!disposed) render();
    }
  }

  function onClick(e: MouseEvent): void {
    const el = (e.target as HTMLElement).closest('[data-action]') as HTMLElement | null;
    if (!el) return;
    const action = el.dataset.action as string;

    if (action === 'exit') {
      closeModal();
      onExit?.();
      return;
    }
    if (action === 'pick-pane') {
      // A dialog must not outlive the pane that opened it.
      closeModal();
      state.activePane = el.dataset.pane as PaneId;
      render();
      void loadPane(state.activePane);
      return;
    }
    BY_ID.get(state.activePane)?.onAction?.(action, el, ctx);
  }

  function onSubmit(e: Event): void {
    const form = e.target as HTMLFormElement;
    const kind = form?.dataset?.form;
    if (!kind) return;
    e.preventDefault();
    BY_ID.get(state.activePane)?.onSubmit?.(kind, form, ctx);
  }

  // Delegated like click/submit. Panes opt in; the social builder uses them for
  // drag-to-reorder (pointerdown/keydown on the handle) and live icon preview.
  function onPointerDown(e: PointerEvent): void {
    BY_ID.get(state.activePane)?.onPointerDown?.(e, ctx);
  }
  function onKeyDown(e: KeyboardEvent): void {
    BY_ID.get(state.activePane)?.onKeyDown?.(e, ctx);
  }
  function onChange(e: Event): void {
    BY_ID.get(state.activePane)?.onChange?.(e.target as HTMLElement, ctx);
  }

  const unmountSideMenu = mountSideMenu(root);
  root.addEventListener('click', onClick);
  root.addEventListener('pointerdown', onPointerDown);
  root.addEventListener('keydown', onKeyDown);
  root.addEventListener('change', onChange);
  root.addEventListener('submit', onSubmit);

  render();
  void loadPane(state.activePane);

  return () => {
    disposed = true;
    closeModal();
    unmountSideMenu();
    root.removeEventListener('click', onClick);
    root.removeEventListener('pointerdown', onPointerDown);
    root.removeEventListener('keydown', onKeyDown);
    root.removeEventListener('change', onChange);
    root.removeEventListener('submit', onSubmit);
  };
}
